import React from "react";
import { Formik } from "formik";
import { Button, Form, Modal } from "react-bootstrap";
import * as Yup from "yup";
import { LoadingSpinner } from "../../common/Loading/LoadingSpinner";
import { FormField, FormAlert, DateTimeFields } from "../../forms";
import { useEventAdd } from "../../../hooks";

type Props = {
    show: boolean;
    onClose: (added: boolean) => void;
    dateTimeStart?: Date;
    dateTimeEnd?: Date;
    isAllDay?: boolean;
    taskId?: string;
    identityIds?: string[];
    isTaskEvent?: boolean;
};

const EventAdd: React.FC<Props> = ({
    show,
    onClose,
    dateTimeStart,
    dateTimeEnd,
    isAllDay,
    taskId,
    identityIds,
    isTaskEvent = false,
}) => {
    const {
        alertVariant,
        currentUser,
        allAppointmentTypes,
        allIdentities,
        handleSubmit,
        getInitialValues,
    } = useEventAdd(taskId, isTaskEvent, identityIds);

    const handleClose = () => onClose(false);

    const onSubmit = async (values: any, formikHelpers: any) => {
        const success = await handleSubmit(values, formikHelpers);
        if (success) {
            setTimeout(() => onClose(true), 1000);
        }
    };

    return (
        <Modal centered show={show} keyboard={false} onHide={handleClose} size="lg">
            <Formik
                initialValues={getInitialValues(dateTimeStart, dateTimeEnd, isAllDay)}
                validationSchema={Yup.object().shape({
                    title: Yup.string().required("Title is required"),
                    description: Yup.string().required("Description is required"),
                    appointmentTypeId: Yup.string().required("Appointment Type is required"),
                    selectedIdentityIds: Yup.array()
                        .min(1, "Minimum of 1 user is required")
                        .required("required"),
                })}
                onSubmit={onSubmit}
            >
                {({
                    values,
                    errors,
                    status,
                    touched,
                    isSubmitting,
                    handleSubmit,
                    setFieldValue,
                }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Header closeButton>
                            <Modal.Title>Add Event</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <FormField
                                name="title"
                                label="Event Name"
                                required
                                placeholder="Enter event name"
                            />
                            
                            <FormField
                                name="description"
                                label="Event Description"
                                required
                                placeholder="Enter event description"
                            />

                            <FormField
                                name="appointmentTypeId"
                                showAppointmentType={!taskId && !isTaskEvent}
                                allAppointmentTypes={allAppointmentTypes}
                                eventToBeUpdated={undefined}
                                setFieldValue={setFieldValue}
                                required
                            />

                            <FormField
                                name="selectedIdentityIds"
                                showUserSelection={!identityIds?.length}
                                allIdentities={allIdentities}
                                identityIds={identityIds}
                                setFieldValue={setFieldValue}
                                required
                            />

                            <FormField
                                name="color"
                                type="color"
                                showColorPicker={true}
                                currentUser={currentUser}
                            />
                            
                            <DateTimeFields
                                errors={errors}
                                touched={touched}
                                values={values}
                                setFieldValue={setFieldValue}
                            />

                            {status && (
                                <FormAlert message={status} variant={alertVariant as 'danger' | 'success' | 'warning' | 'info'} />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="form-group">
                                <Button
                                    variant="primary"
                                    disabled={isSubmitting}
                                    type="submit"
                                    className="me-2"
                                >
                                    {isSubmitting ? (
                                        <LoadingSpinner text="Creating event..." />
                                    ) : (
                                        "Create event"
                                    )}
                                </Button>
                                <Button variant="secondary" onClick={handleClose}>
                                    Cancel
                                </Button>
                            </div>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export { EventAdd };
